/**
 * Date utilities for shared use (e.g. closed-month logic).
 */

const MS_PER_DAY = 86400000;

/** Parse HTML date input value (YYYY-MM-DD) as local calendar date. */
export function parseYmdToLocalDate(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }
  return dt;
}

/** Format local Date as YYYY-MM-DD for date inputs. */
export function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDaysLocal(d: Date, days: number): Date {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + days);
  return x;
}

/** Inclusive day count from fromYmd through toYmd, or null if invalid. */
export function inclusiveDaySpan(
  fromYmd: string,
  toYmd: string,
): number | null {
  const from = parseYmdToLocalDate(fromYmd);
  const to = parseYmdToLocalDate(toYmd);
  if (!from || !to) return null;
  const diff = Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
  return diff + 1;
}

/** Max inclusive calendar-day span allowed for Excel export date pickers (frontend). */
export const EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS = 90;

/**
 * Validates export modal date range: both required, from ≤ to, max inclusive span.
 * @returns Error message or null if valid.
 */
export function validateExportDateRange(
  fromYmd: string,
  toYmd: string,
  maxInclusiveDays: number = EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS,
): string | null {
  const from = fromYmd.trim();
  const to = toYmd.trim();
  if (!from || !to) {
    return "Please select both from and to dates.";
  }
  const span = inclusiveDaySpan(from, to);
  if (span === null) {
    return "Please enter valid dates.";
  }
  if (span < 1) {
    return "To date must be on or after from date.";
  }
  if (span > maxInclusiveDays) {
    return `Date range cannot exceed ${maxInclusiveDays} days.`;
  }
  return null;
}

/**
 * Converts a date (Date, ISO string, or YYYY-MM-DD) to month-year string "YYYY-MM".
 * Used to align with month-end checklist API (GET /api/month-end/checklists/progress?month=YYYY-MM).
 *
 * @param date - Date instance, ISO string, or "YYYY-MM-DD" string
 * @returns "YYYY-MM" or empty string if invalid
 */
export function dateToMonthYear(date: Date | string): string {
  let d: Date;
  if (date instanceof Date) {
    d = date;
  } else if (typeof date === "string") {
    d = new Date(date);
  } else {
    return "";
  }
  if (isNaN(d.getTime())) {
    return "";
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

/**
 * Format an instant in India Standard Time (Asia/Kolkata) as DD/MM/YYYY and 12-hour time with am/pm.
 * Used for approval cards and similar UI where business users expect IST.
 */
export function formatDateTimeInIST(
  value: string | Date | null | undefined,
): string {
  if (value == null || value === "") {
    return "—";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const dateStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
  const timeStr = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
  return `${dateStr}, ${timeStr}`;
}
